import * as Sentry from "@sentry/react";

import { Ingress, StyledAlert, WrapLgMarginBottom } from "App/global-style";
import { Form, FormikProps } from "formik";
import {
  CUSTOMER_MAX_AGE,
  CUSTOMER_MIN_AGE,
} from "../sopSicknessLeaveConstants";

import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { CheckPriceProps } from "Components/Features/CheckPrice/checkPriceConstants";
import { getSopSicknessLeaveParams } from "Components/Features/CheckPrice/checkPriceQueryParamUtils";
import { AlertType } from "Components/Form/Alert";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert";
import { Pep } from "Components/Form/CommonFormElements/CitizenshipPepFormElement/Pep";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NumericInput } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { VALUE_TYPE } from "Components/NumberValue";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { SopCalculator } from "Components/SopCalculator/SopCalculator";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { useCustomerDateOfBirth } from "Store/userIdentification/userIdentificationHooks";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import { getError } from "Utils/productUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP } from "..";
import { customerIsWithinAgeRange } from "../../../RiskProducts/riskProductsUtils";
import { WaitingPeriodBeforePayoutStarts } from "../sopSicknessLeaveInterfaces";
import { getCustomerAge } from "../sopSicknessLeaveUtils";
import { SopSicknessLeaveProductIntroFormValues } from "./sopSicknessLeaveProductIntroInterfaces";
import { getInitialFormState } from "./sopSicknessLeaveProductIntroUtils";
import { validationSchema } from "./sopSicknessLeaveProductIntroValidation";

export const SopSicknessLeaveProductIntro = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const params = getSopSicknessLeaveParams();
  const customerDateOfBirth = useCustomerDateOfBirth();
  const age = getCustomerAge(customerDateOfBirth);
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);
  const showSOPCalculator = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_2245_SOP_CALCULATOR,
  );

  const initialFormState = useFormState<SopSicknessLeaveProductIntroFormValues>(
    SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.id,
    getInitialFormState(params),
  );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.SOP_SICKNESS_LEAVE,
  );

  if (
    !customerIsWithinAgeRange(
      customerDateOfBirth,
      CUSTOMER_MAX_AGE,
      CUSTOMER_MIN_AGE,
    )
  ) {
    return (
      <StyledAlert
        title={getText(
          "sopsicknessleaveform.alerttitle.customernotwithinagerange",
        )}
        type="error"
        alertType={AlertType.STOPPER_ALERT}
        fluid
        marginTop="32px"
      />
    );
  }

  const handleSubmit = (form: SopSicknessLeaveProductIntroFormValues): void => {
    const revenue = form.revenue ?? 0;
    dispatch(
      updatePriceCalculationFormState({
        stepId: SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.id,
        formState: {
          ...form,
          revenue: revenue === 0 ? undefined : revenue,
        },
      }),
    );

    updatePriceRequest({
      productPriceUrl: BackendProductInfo.SOP_SICKNESS_LEAVE.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        age: age,
        dailyPayoutAmount: form.dailyPayoutAmount,
        waitingPeriodBeforePayoutStarts:
          WaitingPeriodBeforePayoutStarts.SIXTEEN_DAYS,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE}`,
        );
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.SOP_SICKNESS_LEAVE}
        validationSchema={validationSchema()}
        header={SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.title}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<SopSicknessLeaveProductIntroFormValues>) => {
          return (
            <Form>
              <WrapLgMarginBottom>
                <Ingress>
                  {getText("sopsicknessleaveform.ingress.aboutyou")}
                </Ingress>
              </WrapLgMarginBottom>

              <StyledAlert
                type="info"
                title={getText("sopsicknessleaveform.alerttitle.waitingperiod")}
                description={getText(
                  "sopsicknessleaveform.alertdescription.waitingperiod",
                )}
                marginBottom="24px"
              />

              <NumericInput
                name="dailyPayoutAmount"
                id="dailyPayoutAmount"
                label={getText("sopsicknessleaveform.label.dailypayoutamount")}
                hint={getText(
                  "sopsicknessleaveform.hint.dailypayoutamountshort",
                )}
                error={getError("dailyPayoutAmount", formProps)}
                inputMode={InputMode.NUMERIC}
                inputProps={{ autoComplete: "off", maxLength: 4 }}
                suffix={VALUE_TYPE.NOK}
              />

              {showSOPCalculator && <SopCalculator />}
              <Pep formProps={formProps} />
              <PartnerProductIntroForms />
              {!isAdvisor && <MarketingConsent />}
              <ScrollToFirstError />
              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleComponents,
                )}
                isVisible
              />
              <BackendErrorAlert priceCalculation={priceCalculationResult} />
              <NavigationButtons hidePreviousButton />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE}
                stepId={SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
